import PageHeader from "../components/shell/PageHeader";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isAuthBypassed } from "../utils/session";
import { helpRoute } from "./help/helpTopics";
import {
  Activity,
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy as CopyIcon,
  Crown,
  Eye,
  EyeOff,
  FileDown,
  Filter,
  Info,
  KeyRound,
  LogOut,
  Plus,
  Repeat2,
  Save,
  Search,
  Shield,
  Tag,
  Trash2,
  Users,
  Wifi,
  WifiOff,
  X,
  XCircle,
  Check,
} from "lucide-react";

import {
  apiCopyMe,
  apiCopyMasters,
  apiCopySubscribe,
  apiCopyMasterEnable,
  apiCopyMasterMe,
  apiCopyUnsubscribe,
  apiCopyMasterUpdateProfile,
  apiCopyMasterPublicTokens,
  apiCopyMasterCreatePublicToken,
  apiCopyMasterDeletePublicToken,
  apiAgentRegisterExisting,
  apiAgentStatus,
  apiLogout,
} from "../api/api";

/* ============================== Utils ============================== */
const cn = (...a) => a.filter(Boolean).join(" ");
const safeNum = (v, f = 0) => (Number.isFinite(+v) ? +v : f);
const safeStr = (v, f = "") => (typeof v === "string" ? v : f);

const AUTH_TOKEN_KEYS = [
  "token",
  "access_token",
  "authToken",
  "jwt",
  "jwtToken",
  "fullmetrix_token",
  "fullmetrix:token",
  "fullmetrix:auth:token",
  "LS_TOKEN",
];
const AUTH_USER_KEYS = ["user", "auth_user", "currentUser", "fullmetrix_user", "fullmetrix:auth:user"];

function safeStorageGet(key) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return "";
    return window.localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function safeStorageSet(key, value) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(key, value);
  } catch {}
}

function safeStorageRemove(key) {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(key);
  } catch {}
}

function parseJsonSafe(value) {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function normalizeBase64Url(value) {
  const normalized = String(value || "").replace(/-/g, "+").replace(/_/g, "/");
  const padding = normalized.length % 4 ? "=".repeat(4 - (normalized.length % 4)) : "";
  return normalized + padding;
}

function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    return JSON.parse(window.atob(normalizeBase64Url(parts[1])));
  } catch {
    return null;
  }
}

function readAuthToken() {
  for (const key of AUTH_TOKEN_KEYS) {
    const value = safeStorageGet(key).trim();
    if (value && value.split(".").length >= 2) return value;
  }

  try {
    if (typeof window === "undefined" || !window.localStorage) return "";
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      const value = safeStorageGet(key).trim();
      if (value && value.split(".").length >= 2 && decodeJwtPayload(value)) return value;
    }
  } catch {}

  return "";
}

function readAuthUserFromStorage() {
  for (const key of AUTH_USER_KEYS) {
    const parsed = parseJsonSafe(safeStorageGet(key));
    if (parsed && typeof parsed === "object") return parsed;
  }
  return null;
}

function sanitizeStoragePart(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_.@-]/gi, "_")
    .slice(0, 96);
}

function getAuthScope() {
  const token = readAuthToken();
  const claims = decodeJwtPayload(token) || {};
  const storedUser = readAuthUserFromStorage() || {};

  const identity =
    claims.sub ||
    claims.user_id ||
    claims.id ||
    claims.email ||
    claims.username ||
    storedUser.id ||
    storedUser.user_id ||
    storedUser.email ||
    storedUser.username ||
    "";

  if (!identity) return "guest";
  return `user_${sanitizeStoragePart(identity)}`;
}

function scopedKey(scope, key) {
  return `fullmetrix:${scope || "guest"}:${key}`;
}

function readScopedPreference(scope, key, fallback) {
  const scoped = safeStorageGet(scopedKey(scope, key));
  if (scoped) return scoped;
  return fallback;
}

function writeScopedPreference(scope, key, value) {
  if (!scope || scope === "guest") return;
  safeStorageSet(scopedKey(scope, key), String(value));
}

function readScopedAgentToken(scope) {
  if (!scope || scope === "guest") return "";
  return safeStorageGet(scopedKey(scope, "agent_token"));
}

const fmtPct = (v, d = 2) => (Number.isFinite(+v) ? `${(+v).toFixed(d)}%` : "—");
const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const d = typeof value === "number"
    ? new Date(value < 1e12 ? value * 1000 : value)
    : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};
const fmtDate = (iso) => {
  const v = iso ? new Date(iso) : null;
  if (!v || Number.isNaN(v.getTime())) return "—";
  return v.toLocaleString();
};
const maskToken = (t) => {
  const s = String(t || "");
  if (!s) return "—";
  if (s.length <= 14) return s;
  return `${s.slice(0, 7)}…${s.slice(-5)}`;
};

function isUnauthorizedPayload(payloadOrErr) {
  const status = payloadOrErr?.status;
  const msg = String(payloadOrErr?.error || payloadOrErr?.message || payloadOrErr?.msg || "");
  return status === 401 || /(unauthori|non autoris|expir|forbidden|401)/i.test(msg);
}

function humanizeApiError(payloadOrErr, t) {
  const status = payloadOrErr?.status;
  const msg = payloadOrErr?.error || payloadOrErr?.message || t("copyTrading.errors.unknown");
  if (status === 401) return { title: t("copyTrading.errors.sessionExpiredTitle"), text: t("copyTrading.errors.sessionExpiredText"), tone: "warn" };
  if (status === 404)
    return {
      title: t("copyTrading.errors.apiUnavailableTitle"),
      text: t("copyTrading.errors.apiUnavailableText"),
      tone: "warn",
    };
  return { title: t("copyTrading.errors.genericTitle"), text: String(msg), tone: "bad" };
}

async function copyToClipboardSmart(text) {
  const value = String(text || "");
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

/* ============================== Agents (PUBLIC) ============================== */
const AGENTS_PUBLIC = {
  follower: { path: "/agents/CopyFollower.ex5", filename: "CopyFollower.ex5" },
};

// Adresse à déclarer dans la liste WebRequest de MT5 : sans elle l'agent
// copieur se lance mais ne joint jamais la plateforme.
const PLATFORM_URL = "https://ia.fullmargin.net";

function downloadFromPublic(path, filename) {
  const url = String(path || "");
  if (!url) return;
  const a = document.createElement("a");
  a.href = url;
  if (filename) a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/* ============================== Followers display ============================== */
const clamp01 = (x) => Math.max(0, Math.min(1, x));

function hashSeed(str) {
  const s = String(str ?? "");
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function seededRand(seed) {
  let x = seed >>> 0;
  x ^= x << 13;
  x ^= x >>> 17;
  x ^= x << 5;
  return (x >>> 0) / 4294967295;
}

const isFakeMaster = (m) => !!m?.is_fake || String(m?.id || "").startsWith("fake-");

// ✅ Followers:
// - fictifs: fallback déterministe 10..20
// - réels:   uniquement la valeur réelle renvoyée par l’API
function followersDisplay(m) {
  const apiVal = safeNum(m?.followers ?? m?.followers_count, 0);
  if (!isFakeMaster(m)) return Math.max(0, Math.round(apiVal));

  if (apiVal >= 1) return Math.round(apiVal);

  const id = String(m?.id ?? "");
  const nick = safeStr(m?.nickname ?? "");
  const seed = hashSeed(`${id}:${nick}:followers`);
  const r = clamp01(seededRand(seed));

  return 10 + Math.round(r * (20 - 10));
}

function getCreatedAtMs(m) {
  const raw = m?.created_at || m?.createdAt || m?.created || m?.created_time || m?.createdTime || null;
  if (!raw) return 0;
  const d = raw instanceof Date ? raw : new Date(raw);
  const ms = d?.getTime?.();
  return Number.isFinite(ms) ? ms : 0;
}

/* ============================== Masters fictifs (NON COPIABLES) ============================== */
const FAKE_MASTERS = [
  {
    id: "fake-001",
    is_fake: true,
    name: "Gold Pulse Pro",
    nickname: "goldpulse",
    style: "Scalping XAUUSD • Sessions London/NY",
    bio: "Stratégie agressive sur impulsions et retests. Gestion stricte du risque.",
    roi_30d: 18.6,
    dd_max: 7.9,
    trades: 146,
    followers: 0,
    agent_connected: true,
  },
  {
    id: "fake-002",
    is_fake: true,
    name: "FX Structure Lab",
    nickname: "structurelab",
    style: "SMC • Break & Retest • EURUSD",
    bio: "Trading structurel: BOS/CHOCH, zones de liquidité, validation multi-TF.",
    roi_30d: 12.3,
    dd_max: 6.2,
    trades: 92,
    followers: 0,
    agent_connected: true,
  },
  {
    id: "fake-003",
    is_fake: true,
    name: "Volatility Edge",
    nickname: "volaedge",
    style: "Indices synthétiques • V75/V100",
    bio: "Exécution rapide, micro-ranges, sorties partielles et trailing dynamique.",
    roi_30d: 22.1,
    dd_max: 10.4,
    trades: 188,
    followers: 0,
    agent_connected: false,
  },
  {
    id: "fake-004",
    is_fake: true,
    name: "Session Sniper",
    nickname: "sessionsniper",
    style: "News-safe • Pullbacks • Risk 0.5%/trade",
    bio: "Approche prudente, évite les pics news, préfère les retours de prix.",
    roi_30d: 9.8,
    dd_max: 4.5,
    trades: 61,
    followers: 0,
    agent_connected: true,
  },
  {
    id: "fake-005",
    is_fake: true,
    name: "Trend Rider 200",
    nickname: "trendrider200",
    style: "EMA 20/50/200 • Trend Following",
    bio: "Suit les tendances fortes, filtres de volatilité, pas de sur-trading.",
    roi_30d: 14.2,
    dd_max: 5.8,
    trades: 74,
    followers: 0,
    agent_connected: false,
  },
  {
    id: "fake-006",
    is_fake: true,
    name: "Range Hunter",
    nickname: "rangehunter",
    style: "Ranges • Mean Reversion • Sessions calmes",
    bio: "Exploite les ranges: extrêmes, confirmations, take profit en paliers.",
    roi_30d: 11.1,
    dd_max: 5.1,
    trades: 103,
    followers: 0,
    agent_connected: true,
  },
  {
    id: "fake-007",
    is_fake: true,
    name: "Smart Liquidity",
    nickname: "smartliq",
    style: "Liquidity Sweeps • SMC • Multi-TF",
    bio: "Se base sur les balayages de liquidité et la structure HTF/LTF.",
    roi_30d: 16.9,
    dd_max: 8.3,
    trades: 119,
    followers: 0,
    agent_connected: true,
  },
  {
    id: "fake-008",
    is_fake: true,
    name: "NY Reversal Desk",
    nickname: "nyreversal",
    style: "Reversal • NY Open • XAUUSD/EURUSD",
    bio: "Scénarios de renversement contrôlé au NY Open, stops serrés.",
    roi_30d: 13.7,
    dd_max: 6.9,
    trades: 85,
    followers: 0,
    agent_connected: false,
  },
];

/* ============================== ROI chart helpers ============================== */
function extractRoiSeries(m) {
  const cand = m?.roi_curve_30d || m?.roi_curve || m?.roi_series || m?.roiHistory || m?.roi_history || null;

  if (Array.isArray(cand) && cand.length >= 6) {
    const vals = cand
      .map((p) => {
        if (typeof p === "number") return p;
        if (p && typeof p === "object") return p.v ?? p.value ?? p.roi ?? p.y ?? null;
        return null;
      })
      .filter((x) => Number.isFinite(+x))
      .map((x) => +x);
    if (vals.length >= 6) return vals;
  }
  return null;
}

function buildSyntheticRoiSeries({ id, targetPct, n = 30 }) {
  const tgt = Number.isFinite(+targetPct) ? +targetPct : 0;
  const seed0 = hashSeed(`${id ?? "x"}:${tgt}:${n}`);
  const arr = [];
  let acc = 0;
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1 || 1);
    const r = seededRand(seed0 + i * 97);
    const noise = (r - 0.5) * 0.18;
    const drift = tgt * t;
    acc = acc * 0.72 + (drift + noise) * 0.28;
    arr.push(acc);
  }
  arr[arr.length - 1] = tgt;
  return arr;
}

function RoiCurveChart({ series, idPrefix = "roi" }) {
  const values = Array.isArray(series) && series.length >= 6 ? series : [];

  const stats = useMemo(() => {
    if (!values.length) return { min: 0, max: 1, last: 0 };
    let min = Infinity,
      max = -Infinity;
    for (const v of values) {
      min = Math.min(min, v);
      max = Math.max(max, v);
    }
    const last = values[values.length - 1];
    if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1, last: 0 };
    if (min === max) {
      min -= 0.5;
      max += 0.5;
    }
    return { min, max, last };
  }, [values]);

  const paths = useMemo(() => {
    const w = 100;
    const h = 40;
    const pad = 2;
    const n = values.length;
    if (n < 2) return { line: "", area: "" };

    const { min, max } = stats;
    const pts = values.map((v, i) => {
      const x = pad + (i * (w - pad * 2)) / (n - 1);
      const t = clamp01((v - min) / (max - min));
      const y = pad + (h - pad * 2) * (1 - t);
      return { x, y };
    });

    let line = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
    for (let i = 1; i < pts.length; i++) line += ` L ${pts[i].x.toFixed(2)} ${pts[i].y.toFixed(2)}`;

    const area =
      line + ` L ${pts[pts.length - 1].x.toFixed(2)} ${(h - pad).toFixed(2)} L ${pts[0].x.toFixed(2)} ${(h - pad).toFixed(2)} Z`;

    return { line, area };
  }, [values, stats]);

  const gradId = `${idPrefix}-grad`;

  return (
    <div className="w-full overflow-hidden rounded-3xl border border-black/10 bg-white/70 dark:border-white/10 dark:bg-white/[0.04]">
      <div className="h-32 w-full px-3 py-3 sm:h-36">
        <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-full w-full text-emerald-600 dark:text-emerald-300">
          <defs>
            <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="currentColor" stopOpacity="0.22" />
              <stop offset="100%" stopColor="currentColor" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          <g opacity="0.18" className="text-neutral-400 dark:text-white/30">
            <line x1="0" x2="100" y1="10" y2="10" stroke="currentColor" strokeWidth="0.3" />
            <line x1="0" x2="100" y1="20" y2="20" stroke="currentColor" strokeWidth="0.3" />
            <line x1="0" x2="100" y1="30" y2="30" stroke="currentColor" strokeWidth="0.3" />
          </g>

          {paths.area ? <path d={paths.area} fill={`url(#${gradId})`} /> : null}
          {paths.line ? (
            <path d={paths.line} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" />
          ) : null}
        </svg>
      </div>
    </div>
  );
}

/* ============================== Background ============================== */
function AuroraBg() {
  return (
    <>
      {/* LIGHT */}
      <div className="pointer-events-none absolute inset-0 dark:hidden bg-[radial-gradient(70%_60%_at_50%_0%,rgba(124,58,237,0.16),rgba(255,255,255,0)_62%)]" />
      <div className="pointer-events-none absolute inset-0 dark:hidden bg-[radial-gradient(55%_45%_at_0%_90%,rgba(236,72,153,0.10),rgba(255,255,255,0)_60%)]" />
      <div className="pointer-events-none absolute inset-0 dark:hidden bg-[radial-gradient(50%_35%_at_95%_70%,rgba(168,85,247,0.10),rgba(255,255,255,0)_62%)]" />
      <div className="pointer-events-none absolute inset-0 dark:hidden opacity-[0.05] [background-image:linear-gradient(to_right,rgba(0,0,0,.55)_1px,transparent_1px),linear-gradient(to_bottom,rgba(0,0,0,.55)_1px,transparent_1px)] [background-size:34px_34px]" />

      {/* DARK */}
      <div className="pointer-events-none absolute inset-0 hidden dark:block bg-[radial-gradient(70%_60%_at_50%_0%,rgba(124,58,237,0.30),rgba(0,0,0,0)_62%)]" />
      <div className="pointer-events-none absolute inset-0 hidden dark:block bg-[radial-gradient(55%_45%_at_10%_90%,rgba(236,72,153,0.18),rgba(0,0,0,0)_60%)]" />
      <div className="pointer-events-none absolute inset-0 hidden dark:block bg-[radial-gradient(45%_35%_at_95%_70%,rgba(168,85,247,0.16),rgba(0,0,0,0)_62%)]" />
      <div className="pointer-events-none absolute inset-0 hidden dark:block opacity-[0.08] [background-image:linear-gradient(to_right,rgba(255,255,255,.10)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,.10)_1px,transparent_1px)] [background-size:34px_34px]" />
      <div className="pointer-events-none absolute inset-0 hidden dark:block bg-gradient-to-b from-[#0A0523] via-[#07031B] to-[#050214] opacity-90" />
    </>
  );
}

/* ============================== Atoms ============================== */
const Surface = ({ className, children, id }) => (
  <div
    id={id}
    className={cn(
      "relative overflow-hidden rounded-3xl border",
      "border-black/10 bg-white/70  backdrop-blur-xl",
      "dark:border-white/10 dark:bg-white/[0.055] dark:",
      className
    )}
  >
    <div className="pointer-events-none absolute inset-0 opacity-60 dark:opacity-70 [background:radial-gradient(1200px_600px_at_20%_0%,rgba(124,58,237,0.10),transparent_60%)]" />
    <div className="relative">{children}</div>
  </div>
);

const Panel = ({ title, subtitle, icon: Icon, right, children, className, id }) => (
  <Surface id={id} className={className}>
    <div className="p-5 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 min-w-0">
            <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-violet-500/10 ring-1 ring-violet-500/20 dark:bg-violet-500/12 dark:ring-violet-400/20">
              {Icon ? <Icon size={18} className="text-violet-600 dark:text-violet-300" /> : null}
            </div>
            <div className="text-sm sm:text-base font-semibold text-neutral-900 dark:text-white truncate">{title}</div>
          </div>
          {subtitle ? <div className="mt-1.5 text-sm text-neutral-600 dark:text-white/70">{subtitle}</div> : null}
        </div>
        {right}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  </Surface>
);

const Badge = ({ tone = "neutral", icon: Icon, children, className }) => {
  const cls =
    tone === "ok"
      ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100"
      : tone === "warn"
      ? "border-amber-500/20 bg-amber-500/10 text-amber-900 dark:text-amber-100"
      : tone === "bad"
      ? "border-rose-500/20 bg-rose-500/10 text-rose-900 dark:text-rose-100"
      : "border-black/10 bg-black/[0.03] text-neutral-800 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/80";

  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-2xl border px-2.5 py-1 text-[11px] font-semibold", cls, className)}>
      {Icon ? <Icon size={14} /> : null}
      <span className="truncate">{children}</span>
    </span>
  );
};

const Btn = ({ variant = "primary", size = "md", leftIcon: LeftIcon, className, ...props }) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-2xl font-semibold transition select-none " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white " +
    "dark:focus-visible:ring-offset-[#050214] disabled:opacity-55 disabled:cursor-not-allowed";
  const sizes = size === "sm" ? "px-3 py-1.5 text-xs" : size === "lg" ? "px-4 py-2.5 text-sm" : "px-3.5 py-2 text-sm";

  const v =
    variant === "ghost"
      ? "border border-black/10 bg-white/60 text-neutral-900 hover:bg-white/80 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/90 dark:hover:bg-white/[0.08]"
      : variant === "soft"
      ? "border border-black/10 bg-black/[0.02] text-neutral-900 hover:bg-black/[0.04] dark:border-white/10 dark:bg-white/[0.04] dark:text-white/90 dark:hover:bg-white/[0.07]"
      : variant === "danger"
      ? "border border-rose-500/20 bg-rose-500/10 text-rose-900 hover:bg-rose-500/15 dark:text-rose-100"
      : "bg-gradient-to-b from-violet-600 to-violet-700 text-white hover:brightness-110 active:brightness-95 ";

  return (
    <button {...props} className={cn(base, sizes, v, className)}>
      {LeftIcon ? <LeftIcon size={16} /> : null}
      <span className="truncate">{props.children}</span>
    </button>
  );
};

const IconBtn = ({ className, ...props }) => (
  <button
    {...props}
    className={cn(
      "inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-black/10 bg-white/60 text-neutral-900 transition hover:bg-white/80",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white",
      "dark:border-white/10 dark:bg-white/[0.05] dark:text-white/90 dark:hover:bg-white/[0.08] dark:focus-visible:ring-offset-[#050214]",
      className
    )}
  />
);

const Field = ({ label, hint, children }) => (
  <div>
    <div className="flex items-end justify-between gap-2">
      <label className="text-xs font-semibold text-neutral-900 dark:text-white">{label}</label>
      {hint ? <div className="text-[11px] text-neutral-500 dark:text-white/55">{hint}</div> : null}
    </div>
    <div className="mt-2">{children}</div>
  </div>
);

const Input = ({ leftIcon: LeftIcon, className, ...props }) => (
  <div className="relative">
    {LeftIcon ? (
      <LeftIcon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-white/45" />
    ) : null}
    <input
      {...props}
      className={cn(
        "w-full rounded-2xl border border-black/10 bg-white/75 px-3 py-2 text-sm font-medium text-neutral-900 outline-none transition placeholder:text-neutral-400",
        "focus:border-violet-500/35 focus:ring-2 focus:ring-violet-500/10",
        "dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:placeholder:text-white/35 dark:focus:ring-violet-500/15",
        LeftIcon ? "pl-9" : "",
        className
      )}
    />
  </div>
);

const Textarea = ({ className, ...props }) => (
  <textarea
    {...props}
    className={cn(
      "w-full rounded-2xl border border-black/10 bg-white/75 px-3 py-2 text-sm font-medium text-neutral-900 outline-none transition placeholder:text-neutral-400",
      "focus:border-violet-500/35 focus:ring-2 focus:ring-violet-500/10",
      "dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:placeholder:text-white/35 dark:focus:ring-violet-500/15",
      className
    )}
  />
);

/* ============================== Tabs (mobile compact) ============================== */
function Tabs({ value, onChange, options }) {
  return (
    <div className="inline-flex w-full rounded-3xl border border-black/10 bg-white/60 p-1 dark:border-white/10 dark:bg-white/[0.05]">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "flex-1 inline-flex items-center justify-center gap-2 rounded-2xl transition font-semibold",
              // ✅ Mobile compact
              "px-2.5 py-1.5 text-[13px]",
              // ✅ Desktop inchangé
              "sm:px-3 sm:py-2 sm:text-sm",
              active
                ? "bg-gradient-to-b from-violet-600 to-violet-700 text-white "
                : "text-neutral-700 hover:bg-black/[0.04] dark:text-white/80 dark:hover:bg-white/[0.06]"
            )}
          >
            {o.icon ? <o.icon size={16} /> : null}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Banner({ tone = "bad", title, children, onClose }) {
  const { t } = useTranslation();
  const cls =
    tone === "ok"
      ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100"
      : tone === "warn"
      ? "border-amber-500/20 bg-amber-500/10 text-amber-900 dark:text-amber-100"
      : "border-rose-500/20 bg-rose-500/10 text-rose-900 dark:text-rose-100";

  return (
    <div className={cn("mt-3 rounded-3xl border px-4 py-3", cls)} role="alert">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {tone === "ok" ? <CheckCircle2 size={16} /> : tone === "warn" ? <AlertTriangle size={16} /> : <XCircle size={16} />}
            <div className="text-sm font-semibold">{title}</div>
          </div>
          <div className="mt-1 text-sm opacity-90">{children}</div>
        </div>
        {onClose ? (
          <IconBtn type="button" onClick={onClose} aria-label={t("copyTrading.common.close")} className="h-9 w-9">
            <X size={16} className="opacity-80" />
          </IconBtn>
        ) : null}
      </div>
    </div>
  );
}

function Toast({ text, onClose }) {
  const { t } = useTranslation();

  // Devenu une vraie boîte de dialogue : Échap la ferme, et la page ne défile
  // plus derrière elle.
  useEffect(() => {
    if (!text) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev || "";
    };
  }, [text, onClose]);

  if (!text) return null;
  /* Portail vers <body>, comme la modale : posé dans l'arbre de la page, ce
     `fixed` héritait du bloc conteneur d'une carte en `backdrop-blur`, et son
     décalage se mesurait depuis le bas de la carte, pas de la fenêtre. */
  const toast = (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4">
      {/* Voile net, sans flou : le flou repeignait toute la page en mauve. */}
      <div className="absolute inset-0 bg-neutral-950/45 dark:bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg overflow-hidden rounded-[28px] border border-black/10 bg-white dark:border-white/10 dark:bg-[#0b0620]">
        {/* Icône et titre sur la même ligne, message pleine largeur en dessous
            et calé à gauche : centré sous l'icône, un texte de deux lignes se
            lisait comme une légende. */}
        <div className="px-6 pt-6 sm:px-8 sm:pt-7">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                <CheckCircle2 size={20} />
              </span>
              <div className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 dark:text-white/50">
                {t("copyTrading.common.noticeTitle")}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("copyTrading.common.close")}
              className="-mr-2 rounded-2xl p-2 hover:bg-black/5 dark:hover:bg-white/10"
            >
              <X size={16} className="opacity-80" />
            </button>
          </div>

          <div className="mt-4 text-base font-semibold leading-7 text-neutral-900 sm:text-lg dark:text-white">{text}</div>
        </div>

        <div className="mt-6 border-t border-black/10 px-6 py-4 sm:px-8 dark:border-white/10">
          <Btn onClick={onClose} size="lg" className="w-full">
            {t("copyTrading.common.gotIt")}
          </Btn>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return toast;
  return createPortal(toast, document.body);
}

/* ============================== Modal/BottomSheet ============================== */
function BottomSheet({ open, title, subtitle, onClose, children, footer }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev || "";
    };
  }, [open, onClose]);

  /* Rendu dans <body> par portail, pour deux raisons.
     1. La page empile ses sections dans des conteneurs `z-20` / `z-50` : un
        `z-[70]` posé à l'intérieur reste prisonnier du sien, et la barre
        d'onglets Master/Copieur passait par-dessus la modale.
     2. Les cartes portent `backdrop-blur`, ce qui établit un bloc conteneur
        pour les descendants `fixed` — `inset-0` ne visait plus la fenêtre. */
  const sheet = (
    <div className={cn("fixed inset-0 z-[70] transition", open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0")}>
      {/* Voile net et sans flou : le flou mélangeait le dégradé violet du fond
          et repeignait toute la page en mauve délavé. */}
      <div className={cn("absolute inset-0 bg-neutral-950/55 transition-opacity dark:bg-black/70", open ? "opacity-100" : "opacity-0")} onClick={onClose} />
      <div className="absolute inset-0 flex items-end sm:items-center sm:justify-center">
        <div
          className={cn(
            "w-full sm:w-[min(760px,calc(100%-1.5rem))] mx-0 sm:mx-3 mb-0 sm:mb-3",
            "transform transition-all",
            open ? "translate-y-0 sm:scale-100 opacity-100" : "translate-y-full sm:scale-95 opacity-0"
          )}
        >
          {/* Surface pleine : en `bg-white/92` la page continuait de transparaître
              au travers du contenu, illisible. */}
          <div
            className={cn(
              "overflow-hidden rounded-[28px] border border-black/10 bg-white",
              "dark:border-white/10 dark:bg-[#0b0620]",
              "max-h-[calc(100vh-1.25rem)]"
            )}
          >
            <div className="flex items-start justify-between gap-3 px-5 pt-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-neutral-900 dark:text-white truncate">{title}</div>
                {subtitle ? <div className="mt-0.5 text-sm text-neutral-600 dark:text-white/70">{subtitle}</div> : null}
              </div>
              <IconBtn className="h-10 w-10" onClick={onClose} aria-label={t("copyTrading.common.close")}>
                <X size={16} className="opacity-80" />
              </IconBtn>
            </div>

            <div className="px-5 pt-3 pb-4 overflow-y-auto max-h-[calc(100vh-11.5rem)]">{children}</div>

            {footer ? <div className="border-t border-black/10 px-5 py-4 dark:border-white/10">{footer}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return sheet;
  return createPortal(sheet, document.body);
}

/* ============================== Detail component ============================== */
function MasterDetail({ m, showRoiChart = false }) {
  const { t } = useTranslation();
  const hasMaster = !!m;
  const mm = m || {};
  const id = String(mm.id ?? "na");
  const online = !!mm.agent_connected;
  const roiVal = useMemo(() => safeNum(mm.roi_30d, 0), [mm.roi_30d]);

  const rawSeries = useMemo(() => (hasMaster ? extractRoiSeries(mm) : null), [hasMaster, m]);

  const series = useMemo(() => {
    if (!hasMaster) return [];
    return rawSeries || buildSyntheticRoiSeries({ id, targetPct: roiVal, n: 30 });
  }, [hasMaster, rawSeries, id, roiVal]);

  const minV = useMemo(() => (series.length ? Math.min(...series) : 0), [series]);
  const maxV = useMemo(() => (series.length ? Math.max(...series) : 0), [series]);
  const lastV = useMemo(() => (series.length ? series[series.length - 1] : roiVal), [series, roiVal]);

  if (!hasMaster) {
    return (
      <div className="rounded-3xl border border-black/10 bg-white/60 p-4 text-sm text-neutral-700 dark:border-white/10 dark:bg-white/[0.045] dark:text-white/75">
        {t("copyTrading.detail.selectPrompt")}
      </div>
    );
  }

  const Kpi = ({ label, value, icon: Icon }) => (
    <div className="min-w-0 rounded-3xl border border-black/10 bg-white/60 px-3 py-2 dark:border-white/10 dark:bg-white/[0.05]">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/55 truncate">{label}</div>
        {Icon ? <Icon size={16} className="opacity-70 text-neutral-700 dark:text-white/70 shrink-0" /> : null}
      </div>
      <div className="mt-1 text-sm font-semibold text-neutral-900 dark:text-white truncate">{value}</div>
    </div>
  );

  return (
    <div className="rounded-3xl border border-black/10 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.045]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-neutral-900 dark:text-white truncate">{mm.name || t("copyTrading.master.number", { id: mm.id })}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {mm.nickname ? (
              <span className="rounded-2xl border border-black/10 bg-white/70 px-2 py-0.5 font-mono text-[11px] font-semibold text-neutral-800 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/85">
                @{mm.nickname}
              </span>
            ) : null}
            {mm.style ? <span className="text-xs text-neutral-600 dark:text-white/65 truncate">{mm.style}</span> : null}
          </div>
        </div>

        <Badge tone={online ? "ok" : "warn"} icon={online ? Wifi : WifiOff} className="shrink-0">
          {online ? t("copyTrading.status.online") : t("copyTrading.status.offline")}
        </Badge>
      </div>

      {mm.bio ? <div className="mt-3 text-sm text-neutral-800 dark:text-white/85 break-words">{mm.bio}</div> : null}

      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Kpi label={t("copyTrading.detail.roi30")} value={fmtPct(mm.roi_30d)} icon={Activity} />
        <Kpi label={t("copyTrading.detail.ddMax")} value={fmtPct(mm.dd_max)} icon={Shield} />
        <Kpi label={t("copyTrading.detail.trades")} value={safeNum(mm.trades, 0)} icon={Repeat2} />
        <Kpi label={t("copyTrading.detail.followers")} value={followersDisplay(mm)} icon={Users} />
      </div>

      {showRoiChart ? (
        <div className="mt-4 rounded-3xl border border-black/10 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.05]">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-neutral-900 dark:text-white">{t("copyTrading.detail.roiCurve")}</div>
              <div className="mt-0.5 text-xs text-neutral-600 dark:text-white/65">
                {t("copyTrading.detail.roiSummary", { last: fmtPct(lastV), min: fmtPct(minV), max: fmtPct(maxV) })}
              </div>
            </div>
            <Badge tone="neutral" icon={Activity} className="shrink-0">
              {t("copyTrading.detail.period30")}
            </Badge>
          </div>

          <div className="mt-3">
            <RoiCurveChart series={series} idPrefix={`roi-${id}`} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SortDropdown({ value, onChange, options }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);

    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "relative w-full rounded-2xl border px-3 py-2 pl-9 pr-10 text-left text-sm font-semibold transition outline-none",
          "border-black/10 bg-white/75 text-neutral-900 hover:bg-white/85",
          "focus:border-violet-500/35 focus:ring-2 focus:ring-violet-500/10",
          "dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:hover:bg-white/[0.08] dark:focus:ring-violet-500/15"
        )}
      >
        <Filter size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-white/45" />
        <span className="block truncate">{selected?.label || t("copyTrading.sort.placeholder")}</span>
        <ChevronDown size={16} className={cn("pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 opacity-75 transition", open ? "rotate-180" : "")} />
      </button>

      <div className={cn("absolute left-0 right-0 z-50 mt-2 origin-top transition", open ? "pointer-events-auto scale-100 opacity-100" : "pointer-events-none scale-95 opacity-0")}>
        <div className={cn("overflow-hidden rounded-2xl border  backdrop-blur-xl", "border-black/10 bg-white/92", "dark:border-white/10 dark:bg-[#060317]/85")}>
          {options.map((o) => {
            const active = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center justify-between gap-3 px-3 py-2 text-sm font-semibold transition",
                  active
                    ? "bg-violet-500/10 text-violet-800 dark:text-violet-200"
                    : "text-neutral-800 hover:bg-black/[0.04] dark:text-white/85 dark:hover:bg-white/[0.06]"
                )}
              >
                <span className="truncate">{o.label}</span>
                {active ? <Check size={16} className="shrink-0 opacity-90" /> : <span className="w-4" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ============================== Page ============================== */
export default function CopyTradingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [authScope, setAuthScope] = useState(() => getAuthScope());
  const authScopeRef = useRef(authScope);
  const requestSeqRef = useRef(0);

  const [mode, setMode] = useState(() => readScopedPreference(getAuthScope(), "copy:mode", "master"));
  useEffect(() => writeScopedPreference(authScope, "copy:mode", mode), [authScope, mode]);

  const [sort, setSort] = useState(() => readScopedPreference(getAuthScope(), "copy:sort", "recent"));
  useEffect(() => writeScopedPreference(authScope, "copy:sort", sort), [authScope, sort]);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);

  const [banner, setBanner] = useState(null);
  const [toast, setToast] = useState("");

  const redirectingRef = useRef(false);
  const handleUnauthorized = useCallback(
    (text = t("copyTrading.errors.sessionExpiredRedirect")) => {
      if (redirectingRef.current) return;

      // DEV bypass : ne pas rediriger vers /sso sur un 401 (backend absent en local).
      if (isAuthBypassed()) {
        setLoading(false);
        setBanner({
          title: t("copyTrading.banner.notConnectedTitle"),
          text: t("copyTrading.banner.notConnectedText"),
          tone: "warn",
        });
        return;
      }

      redirectingRef.current = true;

      setCopierMe(null);
      setMasters([]);
      setMasterMe(null);
      setPublicTokens([]);
      setMasterSharedAgentStatus(null);
      setSelectedMasterId("");
      setSheetOpen(false);
      setLastSyncAt(null);
      setLoading(false);
      setBanner({ title: t("copyTrading.errors.sessionExpiredTitle"), text, tone: "warn" });

      try {
        apiLogout();
      } catch {}

      safeStorageRemove(scopedKey(authScopeRef.current, "agent_token"));
      setTimeout(() => navigate("/sso", { replace: true }), 250);
    },
    [navigate, t]
  );

  // Plus de fermeture automatique : c'est une boîte de dialogue centrée, elle
  // attend une action — bouton, croix, clic sur le voile ou Échap. Un message
  // qui s'évapore au bout de trois secondes se perd dès qu'on regarde ailleurs.
  const showToast = useCallback((m) => {
    setToast(m);
  }, []);

  // Sidebar étapes : pliable + exclusion mutuelle avec la nav principale.
  const [stepsOpen, setStepsOpen] = useState(true);
  // Au montage on ne plie rien : la nav principale doit rester dépliée à chaque
  // actualisation, on ne la plie qu'après une action de l'utilisateur.
  const navExclusionArmed = useRef(false);
  const stateAtMount = useRef(`${mode}:${stepsOpen}`);
  useEffect(() => {
    if (!navExclusionArmed.current) {
      // Tant que l'état n'a pas bougé depuis le montage, on ne fait rien
      // (compatible double-montage StrictMode en dev).
      if (`${mode}:${stepsOpen}` === stateAtMount.current) return;
      navExclusionArmed.current = true;
    }
    if (mode === "copier" && stepsOpen) {
      try { localStorage.setItem("fm:sidebar-collapsed", "true"); } catch {}
      window.dispatchEvent(new Event("fm:sidebar-toggle"));
    }
  }, [stepsOpen, mode]);
  useEffect(() => {
    const onNavToggle = () => {
      let collapsed = true;
      try { collapsed = localStorage.getItem("fm:sidebar-collapsed") !== "false"; } catch {}
      if (!collapsed) setStepsOpen(false);
    };
    window.addEventListener("fm:sidebar-toggle", onNavToggle);
    return () => window.removeEventListener("fm:sidebar-toggle", onNavToggle);
  }, []);

  // Navigation par étapes (sidebar) : scroll + surbrillance + message.
  const [flashStep, setFlashStep] = useState(0);
  const flashStepTimer = useRef(null);
  const goToStep = useCallback((n, message) => {
    const el = document.getElementById(`copy-step-${n}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setFlashStep(n);
    if (message) showToast(message);
    clearTimeout(flashStepTimer.current);
    flashStepTimer.current = setTimeout(() => setFlashStep(0), 1800);
  }, [showToast]);
  useEffect(() => () => clearTimeout(flashStepTimer.current), []);

  // Copier state
  const [copierMe, setCopierMe] = useState(null);
  const [masters, setMasters] = useState([]);
  const [search, setSearch] = useState("");
  const [onlyOnline, setOnlyOnline] = useState(false);
  const [selectedMasterId, setSelectedMasterId] = useState("");

  // Master state
  const [masterMe, setMasterMe] = useState(null);
  const [profile, setProfile] = useState({ name: "", nickname: "", bio: "", style: "", is_public: false });
  const [profileDirty, setProfileDirty] = useState(false);

  // Public tokens (master)
  const [publicTokens, setPublicTokens] = useState([]);
  const [tokensLoading, setTokensLoading] = useState(false);
  const [tokensBusy, setTokensBusy] = useState(false);
  const [revealTokens, setRevealTokens] = useState(false);
  const [newTokLabel, setNewTokLabel] = useState("");

  // Detail modal
  const [sheetOpen, setSheetOpen] = useState(false);

  // Guide complet de l'accès copieur (modale) : les deux puces « Cas d'usage »
  // disent à quoi ça sert, pas comment brancher le terminal qui copie.
  const [accessGuideOpen, setAccessGuideOpen] = useState(false);

  // Header auto-hide / auto-show on scroll
  const [headerVisible, setHeaderVisible] = useState(true);
  const [headerElevated, setHeaderElevated] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);

  const headerRef = useRef(null);
  const lastScrollYRef = useRef(0);
  const scrollTickingRef = useRef(false);

  const copierToken = copierMe?.token || "";
  const activeMasterId = copierMe?.master_id ? String(copierMe.master_id) : "";
  const copierAgentOnline = !!copierMe?.agent_connected;

  const masterIsEnabled = !!masterMe?.is_master;
  const [masterSharedAgentToken, setMasterSharedAgentToken] = useState(() => readScopedAgentToken(getAuthScope()));

  useEffect(() => {
    authScopeRef.current = authScope;
  }, [authScope]);

  useEffect(() => {
    const syncAuthScope = () => {
      const nextScope = getAuthScope();
      setAuthScope((previous) => (previous === nextScope ? previous : nextScope));
    };

    syncAuthScope();
    window.addEventListener("storage", syncAuthScope);
    window.addEventListener("focus", syncAuthScope);
    const intervalId = window.setInterval(syncAuthScope, 1500);

    return () => {
      window.removeEventListener("storage", syncAuthScope);
      window.removeEventListener("focus", syncAuthScope);
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    requestSeqRef.current += 1;
    redirectingRef.current = false;

    setMode(readScopedPreference(authScope, "copy:mode", "master"));
    setSort(readScopedPreference(authScope, "copy:sort", "recent"));
    setCopierMe(null);
    setMasters([]);
    setMasterMe(null);
    setPublicTokens([]);
    setMasterSharedAgentStatus(null);
    setSelectedMasterId("");
    setSheetOpen(false);
    setBanner(null);
    setLastSyncAt(null);
    setMasterSharedAgentToken(readScopedAgentToken(authScope));
  }, [authScope]);

  useEffect(() => {
    const syncMasterSharedToken = () => {
      setMasterSharedAgentToken(readScopedAgentToken(authScope));
    };

    syncMasterSharedToken();
    window.addEventListener("storage", syncMasterSharedToken);
    window.addEventListener("focus", syncMasterSharedToken);
    return () => {
      window.removeEventListener("storage", syncMasterSharedToken);
      window.removeEventListener("focus", syncMasterSharedToken);
    };
  }, [authScope]);

  const [masterSharedAgentStatus, setMasterSharedAgentStatus] = useState(null);

  const masterSharedHeartbeat =
    masterSharedAgentStatus?.last_heartbeat ??
    masterSharedAgentStatus?.lastHeartbeat ??
    masterSharedAgentStatus?.account?.last_heartbeat ??
    null;

  const masterSharedAgentOnline = useMemo(() => {
    const d = toDate(masterSharedHeartbeat);
    if (!d) return false;
    return Date.now() - d.getTime() < 15000;
  }, [masterSharedHeartbeat]);

  const masterAgentOnline = !!masterMe?.agent_connected || masterSharedAgentOnline;

  useEffect(() => {
    let cancelled = false;

    const syncSharedAgentStatus = async () => {
      const token = String(masterSharedAgentToken || "").trim();
      if (!token) {
        if (!cancelled) setMasterSharedAgentStatus(null);
        return;
      }

      try {
        await apiAgentRegisterExisting(token);
        const status = await apiAgentStatus(token);
        if (!cancelled) setMasterSharedAgentStatus(status || null);
      } catch {
        if (!cancelled) setMasterSharedAgentStatus(null);
      }
    };

    syncSharedAgentStatus();
    const id = window.setInterval(syncSharedAgentStatus, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [masterSharedAgentToken]);

  const hydrateProfile = useCallback((me) => {
    setProfile({
      name: safeStr(me?.name || ""),
      nickname: safeStr(me?.nickname || ""),
      bio: safeStr(me?.bio || ""),
      style: safeStr(me?.style || ""),
      is_public: typeof me?.is_public === "boolean" ? me.is_public : false,
    });
    setProfileDirty(false);
  }, []);

  const normalizePublicTokens = useCallback((payload) => {
    const arr =
      (payload && Array.isArray(payload.tokens) && payload.tokens) ||
      (payload && Array.isArray(payload.public_tokens) && payload.public_tokens) ||
      (Array.isArray(payload) ? payload : []);
    return arr
      .filter(Boolean)
      .map((t) => ({
        id: t.id ?? t.token_id ?? t.uuid ?? String(t.token || ""),
        token: t.token || t.value || t.public_token || "",
        label: t.label || t.name || "",
        created_at: t.created_at || t.createdAt || null,
        last_used_at: t.last_used_at || t.lastUsedAt || null,
        revoked: !!(t.revoked || t.disabled || t.is_revoked === true || t.active === false),
      }));
  }, []);

  /* ============================== Tokens API ============================== */
  const loadPublicTokens = useCallback(async (requestScope = authScope) => {
    if (!masterIsEnabled) return;
    setTokensLoading(true);
    try {
      const data = await apiCopyMasterPublicTokens();
      if (authScopeRef.current !== requestScope) return;
      if (data?.error) {
        if (isUnauthorizedPayload(data)) return handleUnauthorized(t("copyTrading.errors.reconnectTokens"));
        return;
      }
      setPublicTokens(normalizePublicTokens(data));
    } catch (e) {
      if (authScopeRef.current !== requestScope) return;
      if (isUnauthorizedPayload(e)) return handleUnauthorized(t("copyTrading.errors.reconnectSso"));
      // silent
    } finally {
      if (authScopeRef.current === requestScope) setTokensLoading(false);
    }
  }, [authScope, masterIsEnabled, normalizePublicTokens, handleUnauthorized, t]);

  const createPublicToken = useCallback(async () => {
    if (!masterIsEnabled) return;
    setTokensBusy(true);
    setBanner(null);
    try {
      const data = await apiCopyMasterCreatePublicToken(newTokLabel.trim());
      if (data?.error) throw data;

      const created = normalizePublicTokens(data);
      if (created.length) {
        setPublicTokens((prev) => {
          const next = [...created, ...prev]; // ✅ nouveaux en haut
          const seen = new Set();
          return next.filter((x) => {
            const k = String(x.id);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          });
        });
      } else {
        await loadPublicTokens();
      }

      setNewTokLabel("");
      showToast(t("copyTrading.toast.publicTokenCreated"));
    } catch (e) {
      if (isUnauthorizedPayload(e)) return handleUnauthorized(t("copyTrading.errors.sessionExpiredReconnect"));
      setBanner(humanizeApiError(e, t));
    } finally {
      setTokensBusy(false);
    }
  }, [masterIsEnabled, newTokLabel, normalizePublicTokens, loadPublicTokens, showToast, handleUnauthorized, t]);

  const revokePublicToken = useCallback(
    async (tok) => {
      if (!masterIsEnabled) return;
      const ok = window.confirm(t("copyTrading.confirm.revokeToken"));
      if (!ok) return;

      setTokensBusy(true);
      setBanner(null);
      try {
        const data = await apiCopyMasterDeletePublicToken(tok?.id);
        if (data?.error) throw data;
        setPublicTokens((prev) => prev.filter((x) => String(x.id) !== String(tok.id)));
        showToast(t("copyTrading.toast.tokenRevoked"));
      } catch (e) {
        if (isUnauthorizedPayload(e)) return handleUnauthorized(t("copyTrading.errors.sessionExpiredReconnect"));
        setBanner(humanizeApiError(e, t));
      } finally {
        setTokensBusy(false);
      }
    },
    [masterIsEnabled, showToast, handleUnauthorized, t]
  );

  /* ============================== Loads ============================== */
  const loadCopier = useCallback(async (requestScope = authScope) => {
    const [meRes, mastersRes] = await Promise.all([apiCopyMe(), apiCopyMasters()]);
    if (authScopeRef.current !== requestScope) return;
    if (meRes?.error) throw meRes;
    if (mastersRes?.error) throw mastersRes;

    setCopierMe(meRes || null);

    const list = Array.isArray(mastersRes?.masters) ? mastersRes.masters : Array.isArray(mastersRes) ? mastersRes : [];
    setMasters(list);

    setSelectedMasterId((prev) => prev || (meRes?.master_id ? String(meRes.master_id) : ""));
  }, [authScope]);

  const loadMaster = useCallback(async (requestScope = authScope) => {
    const meRes = await apiCopyMasterMe();
    if (authScopeRef.current !== requestScope) return;
    if (meRes?.error) throw meRes;
    setMasterMe(meRes || null);
    hydrateProfile(meRes);
  }, [authScope, hydrateProfile]);

  const loadAll = useCallback(async () => {
    const requestScope = authScope;
    const requestSeq = ++requestSeqRef.current;

    setLoading(true);
    setBanner(null);
    setCopierMe(null);
    setMasters([]);
    setMasterMe(null);
    setPublicTokens([]);
    setSelectedMasterId("");
    setLastSyncAt(null);

    try {
      if (mode === "master") await loadMaster(requestScope);
      else await loadCopier(requestScope);

      if (authScopeRef.current !== requestScope || requestSeqRef.current !== requestSeq) return;
      setLastSyncAt(Date.now());
    } catch (e) {
      if (authScopeRef.current !== requestScope || requestSeqRef.current !== requestSeq) return;
      if (isUnauthorizedPayload(e)) return handleUnauthorized(t("copyTrading.errors.yourSessionExpired"));
      setBanner(humanizeApiError(e, t));
    } finally {
      if (authScopeRef.current === requestScope && requestSeqRef.current === requestSeq) setLoading(false);
    }
  }, [authScope, mode, loadCopier, loadMaster, handleUnauthorized, t]);

  useEffect(() => {
    redirectingRef.current = false;
    loadAll();
    setSheetOpen(false);
  }, [loadAll]);

  useEffect(() => {
    if (mode === "master" && masterIsEnabled) loadPublicTokens(authScope);
  }, [authScope, mode, masterIsEnabled, loadPublicTokens]);

  useEffect(() => {
    const measure = () => {
      setHeaderHeight(headerRef.current?.offsetHeight || 0);
    };

    measure();
    window.addEventListener("resize", measure);

    return () => {
      window.removeEventListener("resize", measure);
    };
  }, [banner, mode, syncing, loading, revealTokens, publicTokens.length, masterIsEnabled, masterAgentOnline, copierToken, copierAgentOnline]);

  useEffect(() => {
    const getY = () => window.pageYOffset || document.documentElement.scrollTop || 0;

    lastScrollYRef.current = getY();

    const onScroll = () => {
      if (scrollTickingRef.current) return;
      scrollTickingRef.current = true;

      window.requestAnimationFrame(() => {
        const y = getY();
        const delta = y - lastScrollYRef.current;

        setHeaderElevated(y > 8);

        if (y <= 8) {
          setHeaderVisible(true);
        } else if (delta > 6) {
          setHeaderVisible(false);
        } else if (delta < -4) {
          setHeaderVisible(true);
        }

        lastScrollYRef.current = y;
        scrollTickingRef.current = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  /* ============================== Auto-sync (anti-overlap) ============================== */
  const pollFastInFlight = useRef(false);
  const pollSlowInFlight = useRef(false);

  useEffect(() => {
    let stopped = false;
    let tFast = null;
    let tSlow = null;
    const requestScope = authScope;

    const canRun = () => document.visibilityState === "visible" && authScopeRef.current === requestScope;

    const syncMeFast = async () => {
      if (stopped || !canRun()) return;
      if (pollFastInFlight.current) return;
      pollFastInFlight.current = true;

      setSyncing(true);
      try {
        if (mode === "master") {
          const meRes = await apiCopyMasterMe();
          if (authScopeRef.current !== requestScope) return;
          if (meRes?.error) {
            if (isUnauthorizedPayload(meRes)) return handleUnauthorized();
          } else {
            setMasterMe(meRes || null);
          }
        } else {
          const meRes = await apiCopyMe();
          if (authScopeRef.current !== requestScope) return;
          if (meRes?.error) {
            if (isUnauthorizedPayload(meRes)) return handleUnauthorized();
          } else {
            setCopierMe(meRes || null);
          }
        }
        if (authScopeRef.current === requestScope) setLastSyncAt(Date.now());
      } catch (e) {
        if (authScopeRef.current !== requestScope) return;
        if (isUnauthorizedPayload(e)) return handleUnauthorized();
        // silent
      } finally {
        pollFastInFlight.current = false;
        if (authScopeRef.current === requestScope) setSyncing(false);
      }
    };

    const syncSlow = async () => {
      if (stopped || !canRun()) return;
      if (pollSlowInFlight.current) return;
      pollSlowInFlight.current = true;

      try {
        if (mode === "copier") {
          const mastersRes = await apiCopyMasters();
          if (authScopeRef.current !== requestScope) return;
          if (mastersRes?.error) {
            if (isUnauthorizedPayload(mastersRes)) return handleUnauthorized();
          } else {
            const list = Array.isArray(mastersRes?.masters) ? mastersRes.masters : Array.isArray(mastersRes) ? mastersRes : [];
            setMasters(list);
          }
        } else if (masterIsEnabled) {
          await loadPublicTokens(requestScope);
        }
      } catch (e) {
        if (authScopeRef.current !== requestScope) return;
        if (isUnauthorizedPayload(e)) return handleUnauthorized();
        // silent
      } finally {
        pollSlowInFlight.current = false;
      }
    };

    tFast = setInterval(syncMeFast, 10000);
    tSlow = setInterval(syncSlow, 28000);

    const onVis = () => canRun() && syncMeFast();
    document.addEventListener("visibilitychange", onVis);

    return () => {
      stopped = true;
      if (tFast) clearInterval(tFast);
      if (tSlow) clearInterval(tSlow);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [authScope, mode, masterIsEnabled, loadPublicTokens, handleUnauthorized]);

  /* ============================== Derived ============================== */
  const topStatus = useMemo(() => {
    if (mode === "master") {
      if (!masterIsEnabled) return { tone: "warn", icon: Crown, label: t("copyTrading.topStatus.masterNotEnabled") };
      return {
        tone: masterAgentOnline ? "ok" : "warn",
        icon: masterAgentOnline ? Wifi : WifiOff,
        label: masterAgentOnline ? t("copyTrading.topStatus.masterConnected") : t("copyTrading.topStatus.masterWaiting"),
      };
    }
    if (!copierToken) return { tone: "neutral", icon: AlertTriangle, label: t("copyTrading.topStatus.tokenNotGenerated") };
    return {
      tone: copierAgentOnline ? "ok" : "warn",
      icon: copierAgentOnline ? Wifi : WifiOff,
      label: copierAgentOnline ? t("copyTrading.topStatus.copierConnected") : t("copyTrading.topStatus.copierOffline"),
    };
  }, [mode, masterIsEnabled, masterAgentOnline, copierToken, copierAgentOnline, t]);

  const syncLabel = useMemo(() => {
    if (!lastSyncAt) return "—";
    const delta = Math.max(0, Date.now() - lastSyncAt);
    if (delta < 1200) return t("copyTrading.sync.now");
    const s = Math.round(delta / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.round(s / 60);
    return `${m}min`;
  }, [lastSyncAt, t]);

  // API masters uniquement (réels)
  const marketMasters = useMemo(() => (Array.isArray(masters) ? masters.filter(Boolean) : []), [masters]);

  // ✅ Marketplace = fictifs (pinned en haut) + réels
  const marketplaceMasters = useMemo(() => [...marketMasters, ...FAKE_MASTERS], [marketMasters]);

  const selectedMaster = useMemo(() => {
    const id = selectedMasterId || activeMasterId;
    return marketplaceMasters.find((m) => String(m.id) === String(id)) || null;
  }, [marketplaceMasters, selectedMasterId, activeMasterId]);

  const activeMaster = useMemo(
    () => marketplaceMasters.find((m) => String(m.id) === String(activeMasterId)) || null,
    [marketplaceMasters, activeMasterId]
  );

  const connectedMasterLabel = useMemo(() => {
    if (!activeMaster) return t("copyTrading.master.noneConnected");
    const nm = activeMaster.name || t("copyTrading.master.number", { id: activeMaster.id });
    if (activeMaster.nickname) return `${nm} • @${activeMaster.nickname}`;
    return nm;
  }, [activeMaster, t]);

  const headerTitle = "CopyTrading";

  const headerSubtitle = mode === "master"
    ? masterAgentOnline
      ? t("copyTrading.headerSubtitle.masterOnline")
      : masterIsEnabled
        ? t("copyTrading.headerSubtitle.masterConnectAgent")
        : t("copyTrading.headerSubtitle.masterActivate")
    : activeMaster
      ? copierAgentOnline
        ? t("copyTrading.headerSubtitle.copierOnline", { master: connectedMasterLabel })
        : t("copyTrading.headerSubtitle.copierConnectAgent", { master: connectedMasterLabel })
      : t("copyTrading.headerSubtitle.copierChoose");

  const filteredMasters = useMemo(() => {
    const q = search.trim().toLowerCase();

    const candidates = marketplaceMasters.filter((m) => {
      if (!m) return false;
      const online = !!m.agent_connected;
      if (onlyOnline && !online) return false;
      if (!q) return true;

      const name = safeStr(m.name || "").toLowerCase();
      const nick = safeStr(m.nickname || "").toLowerCase();
      const style = safeStr(m.style || "").toLowerCase();

      return name.includes(q) || nick.includes(q) || style.includes(q) || (`@${nick}`).includes(q);
    });

    const sorters = {
      recent: (a, b) => getCreatedAtMs(b) - getCreatedAtMs(a),
      followers: (a, b) => followersDisplay(b) - followersDisplay(a),
      roi: (a, b) => safeNum(b.roi_30d, -999) - safeNum(a.roi_30d, -999),
      dd: (a, b) => safeNum(a.dd_max, 999) - safeNum(b.dd_max, 999),
      trades: (a, b) => safeNum(b.trades) - safeNum(a.trades),
      name: (a, b) => safeStr(a.name).localeCompare(safeStr(b.name)),
    };

    const sorter = sorters[sort] || sorters.recent;

    const real = candidates.filter((x) => !isFakeMaster(x)).slice().sort(sorter);
    const fake = candidates.filter(isFakeMaster).slice().sort(sorter);

    return [...real, ...fake];
  }, [marketplaceMasters, search, onlyOnline, sort]);

  /* ============================== Actions ============================== */
  const onCopy = useCallback(
    async (value) => {
      const ok = await copyToClipboardSmart(value);
      if (ok) showToast(t("copyTrading.toast.copied"));
      else setBanner({ title: t("copyTrading.errors.clipboardBlockedTitle"), text: t("copyTrading.errors.clipboardBlockedText"), tone: "warn" });
    },
    [showToast, t]
  );

  const onSubscribe = useCallback(
    async (masterId) => {
      const sid = String(masterId ?? "");
      if (!sid) return;

      // ✅ bloque fictifs
      const mm = marketplaceMasters.find((x) => String(x.id) === sid);
      if (isFakeMaster(mm)) {
        setBanner({
          title: t("copyTrading.errors.masterUnavailableTitle"),
          text: t("copyTrading.errors.masterUnavailableText"),
          tone: "warn",
        });
        return;
      }

      setBusy(true);
      setBanner(null);
      try {
        const data = await apiCopySubscribe(masterId);
        if (data?.error) throw data;
        setCopierMe(data);
        setSelectedMasterId(String(masterId));
        showToast(t("copyTrading.toast.connectedTokenGenerated"));
        setSheetOpen(false);
      } catch (e) {
        if (isUnauthorizedPayload(e)) return handleUnauthorized(t("copyTrading.errors.sessionExpiredReconnect"));
        setBanner(humanizeApiError(e, t));
      } finally {
        setBusy(false);
      }
    },
    [showToast, marketplaceMasters, handleUnauthorized, t]
  );

  const onUnsubscribe = useCallback(async () => {
    setBusy(true);
    setBanner(null);
    try {
      const ok = window.confirm(t("copyTrading.confirm.disconnectCopier"));
      if (!ok) return;

      const data = await apiCopyUnsubscribe();
      if (data?.error) throw data;

      setCopierMe(data);
      setSelectedMasterId("");
      showToast(t("copyTrading.toast.disconnected"));
      setSheetOpen(false);

      const mastersRes = await apiCopyMasters();
      if (mastersRes?.error) {
        if (isUnauthorizedPayload(mastersRes)) return handleUnauthorized(t("copyTrading.errors.sessionExpiredReconnect"));
      } else {
        const list = Array.isArray(mastersRes?.masters) ? mastersRes.masters : Array.isArray(mastersRes) ? mastersRes : [];
        setMasters(list);
      }
    } catch (e) {
      if (isUnauthorizedPayload(e)) return handleUnauthorized(t("copyTrading.errors.sessionExpiredReconnect"));
      setBanner(humanizeApiError(e, t));
    } finally {
      setBusy(false);
    }
  }, [showToast, handleUnauthorized, t]);

  const onEnableMaster = useCallback(async () => {
    setBusy(true);
    setBanner(null);
    try {
      const data = await apiCopyMasterEnable(profile);
      if (data?.error) throw data;
      setMasterMe(data);
      hydrateProfile(data);
      showToast(t("copyTrading.toast.masterEnabled"));
      setTimeout(() => loadPublicTokens(), 150);
    } catch (e) {
      if (isUnauthorizedPayload(e)) return handleUnauthorized(t("copyTrading.errors.sessionExpiredReconnect"));
      setBanner(humanizeApiError(e, t));
    } finally {
      setBusy(false);
    }
  }, [profile, hydrateProfile, showToast, loadPublicTokens, handleUnauthorized, t]);

  const onSaveMasterProfile = useCallback(async () => {
    setBusy(true);
    setBanner(null);
    try {
      const data = await apiCopyMasterUpdateProfile(profile);
      if (data?.error) throw data;
      setMasterMe(data);
      hydrateProfile(data);
      showToast(t("copyTrading.toast.profileUpdated"));
    } catch (e) {
      if (isUnauthorizedPayload(e)) return handleUnauthorized(t("copyTrading.errors.sessionExpiredReconnect"));
      setBanner(humanizeApiError(e, t));
    } finally {
      setBusy(false);
    }
  }, [profile, hydrateProfile, showToast, handleUnauthorized, t]);

  /* ============================== UI atoms (inline) ============================== */
  const KpiSmall = ({ label, value, icon: Icon }) => (
    <div className="rounded-3xl border border-black/10 bg-white/60 px-4 py-3 dark:border-white/10 dark:bg-white/[0.05]">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/55 truncate">{label}</div>
        {Icon ? <Icon size={16} className="opacity-70 text-neutral-700 dark:text-white/70 shrink-0" /> : null}
      </div>
      <div className="mt-1.5 text-2xl font-bold tracking-tight tabular-nums text-neutral-900 dark:text-white truncate">{value}</div>
    </div>
  );

  const MasterCard = ({ m }) => {
    const id = String(m.id);
    const online = !!m.agent_connected;
    const isSelected = (selectedMasterId || activeMasterId) === id;

    const openDetails = () => {
      setSelectedMasterId(id);
      setSheetOpen(true);
    };

    const MiniStat = ({ label, value }) => (
      <div className="min-w-0 rounded-2xl border border-black/10 bg-white/70 px-2.5 py-2 text-center dark:border-white/10 dark:bg-white/[0.04]">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/55">{label}</div>
        <div className="mt-0.5 text-sm font-semibold tabular-nums text-neutral-900 dark:text-white">{value}</div>
      </div>
    );

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={openDetails}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " " ? openDetails() : null)}
        className={cn(
          "group rounded-3xl border p-4 transition cursor-pointer select-none",
          "border-black/10 bg-white/60 hover:bg-white/80  ",
          "dark:border-white/10 dark:bg-white/[0.045] dark:hover:bg-white/[0.07] dark:",
          isSelected ? "ring-2 ring-violet-500/20" : ""
        )}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex h-9 w-9 items-center justify-center rounded-2xl ring-1 shrink-0",
                  online
                    ? "bg-emerald-500/10 ring-emerald-500/20 text-emerald-700 dark:text-emerald-200"
                    : "bg-amber-500/10 ring-amber-500/20 text-amber-700 dark:text-amber-200"
                )}
              >
                {online ? <Wifi size={16} /> : <WifiOff size={16} />}
              </span>

              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-neutral-900 dark:text-white">
                  {m.name || t("copyTrading.master.number", { id: m.id })} {isFakeMaster(m) ? <span className="opacity-60"></span> : null}
                </div>

                <div className="mt-0.5 flex flex-wrap items-center gap-2 min-w-0">
                  {m.nickname ? (
                    <span className="max-w-full truncate rounded-2xl border border-black/10 bg-white/70 px-2 py-0.5 font-mono text-[11px] font-semibold text-neutral-800 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/85">
                      @{m.nickname}
                    </span>
                  ) : null}
                  <span className="truncate text-xs text-neutral-600 dark:text-white/65">{m.style || "—"}</span>
                </div>
              </div>
            </div>
          </div>

          <Badge tone={online ? "ok" : "warn"} icon={online ? Wifi : WifiOff} className="shrink-0">
            {online ? t("copyTrading.status.online") : t("copyTrading.status.offline")}
          </Badge>
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <MiniStat label={t("copyTrading.card.roi")} value={fmtPct(m.roi_30d)} />
          <MiniStat label={t("copyTrading.card.dd")} value={fmtPct(m.dd_max)} />
          <MiniStat label={t("copyTrading.card.trades")} value={safeNum(m.trades, 0)} />
        </div>

        <div className="mt-2 rounded-2xl border border-black/10 bg-white/70 p-2 dark:border-white/10 dark:bg-white/[0.04]">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/55">{t("copyTrading.card.followers")}</div>
              <div className="mt-0.5 text-xs font-semibold text-neutral-900 dark:text-white">{followersDisplay(m)}</div>
            </div>
            <Users size={16} className="opacity-70 text-neutral-700 dark:text-white/70 shrink-0" />
          </div>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openDetails();
            }}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-black/10 bg-white/70 px-3 py-2 text-xs font-semibold text-neutral-900 hover:bg-white/85 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/90 dark:hover:bg-white/[0.08]"
          >
            <Info size={16} className="text-violet-600 dark:text-violet-300" />
            {t("copyTrading.actions.open")}
          </button>

          {activeMasterId === id ? (
            <Btn
              variant="soft"
              size="sm"
              leftIcon={CheckCircle2}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedMasterId(id);
              }}
              className="w-full"
            >
              {t("copyTrading.actions.connected")}
            </Btn>
          ) : isFakeMaster(m) ? (
            <Btn
              variant="ghost"
              size="sm"
              leftIcon={AlertTriangle}
              onClick={(e) => e.stopPropagation()}
              disabled
              className="w-full"
            >
              {t("copyTrading.actions.unavailable")}
            </Btn>
          ) : (
            <Btn
              size="sm"
              leftIcon={Repeat2}
              onClick={(e) => {
                e.stopPropagation();
                onSubscribe(m.id);
              }}
              disabled={busy || loading}
              className="w-full"
            >
              {t("copyTrading.actions.connect")}
            </Btn>
          )}
        </div>
      </div>
    );
  };

  /* ============================== Render ============================== */
  return (
    <div className="relative min-h-[calc(100vh-64px)] bg-neutral-50 text-neutral-900 dark:bg-[#050214] dark:text-white">
      <AuroraBg />
      <Toast text={toast} onClose={() => setToast("")} />

      <div className="relative flex items-stretch">
      {mode === "copier" ? (
        <aside
          className={cn(
            "hidden lg:flex shrink-0 sticky top-0 h-[100dvh] flex-col gap-2 border-r border-black/10 bg-white/70 backdrop-blur-xl transition-[width] duration-300 ease-in-out dark:border-white/10 dark:bg-white/[0.04]",
            stepsOpen ? "w-[248px] p-3" : "w-14 p-2"
          )}
        >
          <div className={stepsOpen ? "flex items-center justify-between gap-2 px-1 pt-1" : "flex justify-center pt-1"}>
            {stepsOpen && (
              <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-500 dark:text-white/55">
                {t("copyTrading.steps.title")}
              </span>
            )}
            <button
              type="button"
              onClick={() => setStepsOpen((v) => !v)}
              title={stepsOpen ? t("copyTrading.steps.collapse") : t("copyTrading.steps.expand")}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-black/10 bg-white/70 text-neutral-600 transition hover:bg-white/90 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/80 dark:hover:bg-white/[0.09]"
            >
              {stepsOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            </button>
          </div>

          {[
            { n: 1, label: t("copyTrading.steps.step1Label"), msg: t("copyTrading.steps.step1Msg"), done: !!selectedMasterId },
            { n: 2, label: t("copyTrading.steps.step2Label"), msg: t("copyTrading.steps.step2Msg"), done: !!activeMaster },
            { n: 3, label: t("copyTrading.steps.step3Label"), msg: t("copyTrading.steps.step3Msg"), done: !!copierToken },
          ].map((s) => (
            <button
              key={s.n}
              type="button"
              onClick={() => goToStep(s.n, s.msg)}
              title={s.label}
              className={cn(
                "group flex items-center rounded-2xl border transition",
                stepsOpen ? "gap-3 px-3 py-2.5 text-left" : "mx-auto h-11 w-10 justify-center",
                flashStep === s.n
                  ? "border-violet-500/60 bg-violet-500/10"
                  : "border-black/10 bg-white/70 hover:bg-white/90 dark:border-white/10 dark:bg-white/[0.04] dark:hover:bg-white/[0.07]"
              )}
            >
              <span
                className={cn(
                  "grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold",
                  s.done ? "bg-emerald-500 text-white" : "bg-violet-500/15 text-violet-700 dark:text-violet-300"
                )}
              >
                {s.done ? <CheckCircle2 size={15} /> : s.n}
              </span>
              {stepsOpen && (
                <span className="block truncate text-sm font-semibold text-neutral-900 dark:text-white">{s.label}</span>
              )}
            </button>
          ))}

          {stepsOpen && (
            <div className="mt-auto rounded-2xl border border-black/10 bg-white/60 p-2.5 text-[11px] leading-snug text-neutral-600 dark:border-white/10 dark:bg-white/[0.03] dark:text-white/60">
              {t("copyTrading.steps.hint")}
            </div>
          )}
        </aside>
      ) : null}

      <div className="relative w-full min-w-0 flex-1 px-3 sm:px-6 lg:px-8 2xl:px-12 pb-[calc(env(safe-area-inset-bottom)+26px)]">


{/* Sticky Header */}
<div
  className="relative z-20 transition-[margin-bottom] duration-300 ease-out lg:z-50"
  style={{ marginBottom: headerVisible ? 0 : -(headerHeight || 0) }}
>
  <div
    ref={headerRef}
    className={cn(
      // Mobile : colle SOUS la barre du haut (fixe, z-30) et passe dessous en z,
      // sinon il la recouvre au scroll. Desktop : sticky en haut classique.
      "sticky top-[var(--fm-topbar-h)] z-20 pt-1.5 sm:pt-3 transition-transform duration-300 ease-out will-change-transform lg:top-0 lg:z-50",
      headerVisible
        ? "translate-y-0"
        : "-translate-y-[calc(100%+0.65rem)] sm:-translate-y-[calc(100%+0.85rem)]"
    )}
  >
    <div
      className={cn(
        "rounded-[18px] sm:rounded-[28px] bg-gradient-to-r from-violet-500/28 via-fuchsia-500/18 to-violet-500/28 p-[1px] transition-shadow duration-300",
        headerElevated
          ? " dark:"
          : ""
      )}
    >
      <Surface className="rounded-[17px] sm:rounded-[27px]">
        <div className="px-3 py-2.5 sm:px-6 sm:py-4">
          {/* En-tête commun à toutes les pages (PageHeader). */}
          <PageHeader
            icon={Repeat2}
            title={headerTitle}
            subtitle={headerSubtitle}
            chips={
              <>
                <Badge tone={topStatus.tone} icon={topStatus.icon}>
                  {topStatus.label}
                </Badge>
                <Badge tone="neutral" icon={syncing ? Activity : CheckCircle2}>
                  {syncing ? t("copyTrading.sync.syncing") : t("copyTrading.sync.label", { value: syncLabel })}
                </Badge>
              </>
            }
          />

          <div className="mt-2 sm:mt-4 grid grid-cols-1 gap-1.5 sm:gap-3 sm:grid-cols-[420px_1fr] sm:items-center">
            <Tabs
              value={mode}
              onChange={(v) => {
                setMode(v);
                setSheetOpen(false);
                setBanner(null);
              }}
              options={[
                { value: "master", label: t("copyTrading.tabs.master"), icon: Crown },
                { value: "copier", label: t("copyTrading.tabs.copier"), icon: Users },
              ]}
            />

            <div className="flex items-center justify-between sm:justify-end">
              <span className="text-[11px] sm:text-xs text-neutral-500 dark:text-white/55">
                {loading ? t("copyTrading.header.loading") : t("copyTrading.header.autoSync")}
              </span>
            </div>
          </div>

          {banner ? (
            <Banner tone={banner.tone} title={banner.title} onClose={() => setBanner(null)}>
              {banner.text}
            </Banner>
          ) : null}
        </div>
      </Surface>
    </div>
  </div>
</div>

        {/* ============================== COPIER ============================== */}
        {mode === "copier" ? (
          <>
            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-12">
              {/* Marketplace */}
              <Panel
                id="copy-step-1"
                className={cn("lg:col-span-12 order-1 scroll-mt-28 transition", flashStep === 1 && "ring-2 ring-violet-500/70")}
                title={t("copyTrading.marketplace.title")}
                subtitle={t("copyTrading.marketplace.subtitle")}
                icon={Users}
                right={
                  <Badge tone="neutral" icon={Users}>
                    {filteredMasters.length}
                  </Badge>
                }
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div className="w-full sm:flex-1">
                    <Input leftIcon={Search} value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("copyTrading.marketplace.searchPlaceholder")} />
                  </div>

                  <div className="flex gap-2 sm:w-auto">
                    <div className="w-full sm:w-[200px]">
                      <SortDropdown
                        value={sort}
                        onChange={(v) => setSort(v)}
                        options={[
                          { value: "recent", label: t("copyTrading.sortOptions.recent") },
                          { value: "followers", label: t("copyTrading.sortOptions.followers") },
                          { value: "roi", label: t("copyTrading.sortOptions.roi") },
                          { value: "trades", label: t("copyTrading.sortOptions.trades") },
                          { value: "dd", label: t("copyTrading.sortOptions.dd") },
                          { value: "name", label: t("copyTrading.sortOptions.name") },
                        ]}
                      />
                    </div>

                    <Btn variant={onlyOnline ? "primary" : "ghost"} size="md" leftIcon={Wifi} onClick={() => setOnlyOnline((x) => !x)}>
                      {onlyOnline ? t("copyTrading.marketplace.onlyOnline") : t("copyTrading.marketplace.all")}
                    </Btn>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                  {loading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="h-56 animate-pulse rounded-3xl border border-black/10 bg-white/60 dark:border-white/10 dark:bg-white/[0.05]" />
                    ))
                  ) : filteredMasters.length ? (
                    filteredMasters.map((m) => <MasterCard key={String(m.id)} m={m} />)
                  ) : (
                    <div className="sm:col-span-2 2xl:col-span-3 rounded-3xl border border-black/10 bg-white/60 p-4 text-sm text-neutral-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/75">
                      {t("copyTrading.marketplace.empty")}
                    </div>
                  )}
                </div>
              </Panel>

              {/* Connexion copieur — en haut */}
                <Panel
                  id="copy-step-3"
                  className={cn("lg:col-span-12 order-3 scroll-mt-28 transition", flashStep === 3 && "ring-2 ring-violet-500/70")}
                  title={t("copyTrading.copierStep3.title")}
                  subtitle={t("copyTrading.copierStep3.subtitle")}
                  icon={Repeat2}
                  right={
                    <Badge
                      tone={!copierToken ? "neutral" : copierAgentOnline ? "ok" : "warn"}
                      icon={!copierToken ? AlertTriangle : copierAgentOnline ? Wifi : WifiOff}
                      className="shrink-0 whitespace-nowrap"
                    >
                      {!copierToken ? t("copyTrading.status.noConnection") : copierAgentOnline ? t("copyTrading.status.connected") : t("copyTrading.status.waiting")}
                    </Badge>
                  }
                >
                  <div className="grid items-start gap-3 lg:grid-cols-2">
                    <div className="rounded-3xl border border-black/10 bg-white/70 p-3 dark:border-white/10 dark:bg-white/[0.05]">
                      <div className="flex items-start gap-3">
                        <div
                          className={cn(
                            "flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border",
                            activeMaster
                              ? "border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
                              : "border-amber-200 bg-amber-100 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
                          )}
                        >
                          {activeMaster ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/55">
                            {t("copyTrading.copierStep3.connectedAccount")}
                          </div>

                          <div className="mt-1.5 text-sm font-semibold leading-snug text-neutral-900 dark:text-white break-words">
                            {activeMaster ? connectedMasterLabel : t("copyTrading.copierStep3.noMasterConnected")}
                          </div>

                          <div className="mt-1 text-[13px] leading-snug text-neutral-600 dark:text-white/65">
                            {activeMaster
                              ? t("copyTrading.copierStep3.followingMaster")
                              : t("copyTrading.copierStep3.chooseMasterHint")}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-3xl border border-black/10 bg-white/70 p-3 dark:border-white/10 dark:bg-white/[0.05]">
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-sky-200 bg-sky-100 text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
                          <KeyRound className="h-5 w-5" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-white/55">
                            {t("copyTrading.copierStep3.copierToken")}
                          </div>

                          <div className="mt-2 rounded-2xl border border-black/10 bg-white/80 px-3 py-2 font-mono text-sm font-semibold text-neutral-900 dark:border-white/10 dark:bg-[#050214]/60 dark:text-white">
                            <span className={cn("block truncate", !copierToken && "text-neutral-400 dark:text-white/35")}>
                              {copierToken || t("copyTrading.copierStep3.tokenGeneratedHint")}
                            </span>
                          </div>

                          <div className="mt-3 grid grid-cols-1 gap-2">
                            <Btn variant="ghost" leftIcon={CopyIcon} onClick={() => onCopy(copierToken)} disabled={!copierToken}>
                              {t("copyTrading.copierStep3.copyToken")}
                            </Btn>
                            <Btn leftIcon={FileDown} onClick={() => downloadFromPublic(AGENTS_PUBLIC.follower.path, AGENTS_PUBLIC.follower.filename)}>
                              {t("copyTrading.actions.downloadAgent")}
                            </Btn>
                          </div>

                          {activeMasterId ? (
                            <div className="mt-2">
                              <Btn variant="danger" leftIcon={LogOut} onClick={onUnsubscribe} disabled={busy || loading} className="w-full">
                                {t("copyTrading.copierStep3.disconnectCopier")}
                              </Btn>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </Panel>

                <Panel
                  id="copy-step-2"
                  className={cn("lg:col-span-12 order-2 scroll-mt-28 transition", flashStep === 2 && "ring-2 ring-violet-500/70")}
                  title={t("copyTrading.step2.title")}
                  subtitle={t("copyTrading.step2.subtitle")}
                  icon={Users}
                  right={
                    selectedMaster ? (
                      <Badge tone={selectedMaster?.agent_connected ? "ok" : "warn"} icon={selectedMaster?.agent_connected ? Wifi : WifiOff}>
                        {selectedMaster?.agent_connected ? t("copyTrading.status.online") : t("copyTrading.status.offline")}
                      </Badge>
                    ) : (
                      <Badge tone="neutral" icon={Info}>
                        —
                      </Badge>
                    )
                  }
                >
                  <div className="space-y-3">
                    <MasterDetail m={selectedMaster} showRoiChart={false} />
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Btn variant="ghost" leftIcon={Info} onClick={() => selectedMaster && setSheetOpen(true)} disabled={!selectedMaster} className="w-full">
                        {t("copyTrading.step2.openDetails")}
                      </Btn>
                      {selectedMaster ? (
                        activeMasterId === String(selectedMaster.id) ? (
                          <Btn variant="soft" leftIcon={CheckCircle2} disabled className="w-full">
                            {t("copyTrading.step2.alreadyConnected")}
                          </Btn>
                        ) : isFakeMaster(selectedMaster) ? (
                          <Btn variant="ghost" leftIcon={AlertTriangle} disabled className="w-full">
                            {t("copyTrading.step2.notCopiable")}
                          </Btn>
                        ) : (
                          <Btn leftIcon={Repeat2} onClick={() => onSubscribe(selectedMaster.id)} disabled={busy || loading} className="w-full">
                            {t("copyTrading.actions.connect")}
                          </Btn>
                        )
                      ) : null}
                    </div>
                  </div>
                </Panel>
            </div>
            {/* Détails Master (modal) */}
            <BottomSheet
              open={sheetOpen}
              onClose={() => setSheetOpen(false)}
              title={t("copyTrading.sheet.title")}
              subtitle={selectedMaster ? t("copyTrading.sheet.subtitleSelected") : t("copyTrading.sheet.subtitleNone")}
              footer={
                selectedMaster ? (
                  <div className="grid grid-cols-1 gap-2">
                    {(() => {
                      const fake = isFakeMaster(selectedMaster);

                      if (activeMasterId === String(selectedMaster.id)) {
                        return (
                          <Btn variant="danger" leftIcon={LogOut} onClick={onUnsubscribe} disabled={busy || loading} size="lg">
                            {t("copyTrading.sheet.disconnect")}
                          </Btn>
                        );
                      }

                      if (fake) {
                        return (
                          <Btn variant="ghost" leftIcon={AlertTriangle} disabled size="lg" className="w-full">
                            {t("copyTrading.sheet.fakeNotCopiable")}
                          </Btn>
                        );
                      }

                      return (
                        <Btn leftIcon={Repeat2} onClick={() => onSubscribe(selectedMaster.id)} disabled={busy || loading} size="lg">
                          {t("copyTrading.actions.connect")}
                        </Btn>
                      );
                    })()}

                    <div className="grid grid-cols-2 gap-2">
                      <Btn variant="ghost" leftIcon={CopyIcon} onClick={() => onCopy(selectedMaster.id)} size="lg" disabled={isFakeMaster(selectedMaster)}>
                        {t("copyTrading.sheet.copyId")}
                      </Btn>
                      <Btn variant="ghost" leftIcon={X} onClick={() => setSheetOpen(false)} size="lg">
                        {t("copyTrading.common.close")}
                      </Btn>
                    </div>
                  </div>
                ) : (
                  <Btn variant="ghost" leftIcon={AlertTriangle} disabled size="lg" className="w-full">
                    {t("copyTrading.sheet.noMaster")}
                  </Btn>
                )
              }
            >
              <MasterDetail m={selectedMaster} showRoiChart />
            </BottomSheet>
          </>
        ) : null}

        {/* ============================== MASTER ============================== */}
        {mode === "master" ? (
          <>
            <div className="mt-4">
              <Panel
                title={t("copyTrading.howItWorks.title")}
                subtitle={t("copyTrading.howItWorks.subtitle")}
                icon={Info}
              >
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded-3xl border border-black/10 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.05]">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl border border-violet-200 bg-violet-100 text-violet-700 dark:border-violet-500/20 dark:bg-violet-500/10 dark:text-violet-300">
                      <Crown className="h-5 w-5" />
                    </div>
                    <div className="text-sm font-semibold text-neutral-900 dark:text-white">{t("copyTrading.howItWorks.card1Title")}</div>
                    <div className="mt-1 text-sm text-neutral-600 dark:text-white/70">{t("copyTrading.howItWorks.card1Text")}</div>
                  </div>
                  <div className="rounded-3xl border border-black/10 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.05]">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl border border-sky-200 bg-sky-100 text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">
                      <Repeat2 className="h-5 w-5" />
                    </div>
                    <div className="text-sm font-semibold text-neutral-900 dark:text-white">{t("copyTrading.howItWorks.card2Title")}</div>
                    <div className="mt-1 text-sm text-neutral-600 dark:text-white/70">{t("copyTrading.howItWorks.card2Text")}</div>
                  </div>
                  <div className="rounded-3xl border border-black/10 bg-white/60 p-4 dark:border-white/10 dark:bg-white/[0.05]">
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl border border-emerald-200 bg-emerald-100 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300">
                      <Eye className="h-5 w-5" />
                    </div>
                    <div className="text-sm font-semibold text-neutral-900 dark:text-white">{t("copyTrading.howItWorks.card3Title")}</div>
                    <div className="mt-1 text-sm text-neutral-600 dark:text-white/70">{t("copyTrading.howItWorks.card3Text")}</div>
                  </div>
                </div>
              </Panel>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-12">
              <div className="lg:col-span-7 space-y-4">
                <Panel
                  title={t("copyTrading.profile.title")}
                  subtitle={t("copyTrading.profile.subtitle")}
                  icon={Crown}
                  right={
                    <Badge tone={masterIsEnabled ? "ok" : "warn"} icon={masterIsEnabled ? CheckCircle2 : AlertTriangle}>
                      {masterIsEnabled ? t("copyTrading.profile.ready") : t("copyTrading.profile.toActivate")}
                    </Badge>
                  }
                >
                  <div className="mb-4 rounded-3xl border border-black/10 bg-white/60 px-4 py-3 text-sm text-neutral-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/75">
                    {t("copyTrading.profile.visibleNote")}
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <Field label={t("copyTrading.profile.namePublic")}>
                      <Input
                        value={profile.name}
                        onChange={(e) => {
                          setProfile((p) => ({ ...p, name: e.target.value }));
                          setProfileDirty(true);
                        }}
                        placeholder={t("copyTrading.profile.namePlaceholder")}
                      />
                    </Field>

                    <Field label={t("copyTrading.profile.nickname")} hint={t("copyTrading.profile.nicknameHint")}>
                      <Input
                        leftIcon={Tag}
                        value={profile.nickname}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\s+/g, "");
                          setProfile((p) => ({ ...p, nickname: val }));
                          setProfileDirty(true);
                        }}
                        placeholder={t("copyTrading.profile.nicknamePlaceholder")}
                      />
                    </Field>

                    <Field label={t("copyTrading.profile.style")}>
                      <Input
                        value={profile.style}
                        onChange={(e) => {
                          setProfile((p) => ({ ...p, style: e.target.value }));
                          setProfileDirty(true);
                        }}
                        placeholder={t("copyTrading.profile.stylePlaceholder")}
                      />
                    </Field>

                    <Field label={t("copyTrading.profile.bio")}>
                      <Textarea
                        value={profile.bio}
                        onChange={(e) => {
                          setProfile((p) => ({ ...p, bio: e.target.value }));
                          setProfileDirty(true);
                        }}
                        placeholder={t("copyTrading.profile.bioPlaceholder")}
                        className="min-h-[120px]"
                      />
                    </Field>

                  </div>
                </Panel>

                <Panel
                  title={t("copyTrading.visibility.title")}
                  subtitle={t("copyTrading.visibility.subtitle")}
                  icon={Eye}
                  right={
                    <Badge tone={profile.is_public ? "ok" : "neutral"} icon={profile.is_public ? Eye : EyeOff}>
                      {profile.is_public ? t("copyTrading.visibility.public") : t("copyTrading.visibility.private")}
                    </Badge>
                  }
                >
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => {
                        setProfile((p) => ({ ...p, is_public: false }));
                        setProfileDirty(true);
                      }}
                      className={cn(
                        "rounded-3xl border p-4 text-left transition",
                        !profile.is_public
                          ? "border-violet-500/30 bg-violet-500/10 ring-2 ring-violet-500/10"
                          : "border-black/10 bg-white/60 hover:bg-white/80 dark:border-white/10 dark:bg-white/[0.05] dark:hover:bg-white/[0.08]"
                      )}
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
                        <EyeOff size={16} className="text-neutral-700 dark:text-white/80" />
                        {t("copyTrading.visibility.privateTitle")}
                      </div>
                      <div className="mt-1 text-sm text-neutral-600 dark:text-white/70">{t("copyTrading.visibility.privateText")}</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setProfile((p) => ({ ...p, is_public: true }));
                        setProfileDirty(true);
                      }}
                      className={cn(
                        "rounded-3xl border p-4 text-left transition",
                        profile.is_public
                          ? "border-emerald-500/30 bg-emerald-500/10 ring-2 ring-emerald-500/10"
                          : "border-black/10 bg-white/60 hover:bg-white/80 dark:border-white/10 dark:bg-white/[0.05] dark:hover:bg-white/[0.08]"
                      )}
                    >
                      <div className="flex items-center gap-2 text-sm font-semibold text-neutral-900 dark:text-white">
                        <Eye size={16} className="text-emerald-700 dark:text-emerald-300" />
                        {t("copyTrading.visibility.publicTitle")}
                      </div>
                      <div className="mt-1 text-sm text-neutral-600 dark:text-white/70">{t("copyTrading.visibility.publicText")}</div>
                    </button>
                  </div>

                  {/* Enregistrement placé APRÈS le choix de visibilité : on
                      choisit d'abord le statut du compte, on enregistre ensuite. */}
                  <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {!masterIsEnabled ? (
                      <Btn leftIcon={Crown} onClick={onEnableMaster} disabled={busy || loading}>
                        {t("copyTrading.profile.enableMaster")}
                      </Btn>
                    ) : (
                      <Btn leftIcon={Save} onClick={onSaveMasterProfile} disabled={busy || loading || !profileDirty}>
                        {t("copyTrading.profile.saveProfile")}
                      </Btn>
                    )}
                    <Btn variant="ghost" leftIcon={Info} onClick={() => showToast(t("copyTrading.toast.profileReadyInfo"))}>
                      {t("copyTrading.profile.info")}
                    </Btn>
                  </div>
                </Panel>
              </div>

              <div className="lg:col-span-5 space-y-4">
                <Panel
                  title={t("copyTrading.access.title")}
                  subtitle={t("copyTrading.access.subtitle")}
                  icon={KeyRound}
                  right={
                    <div className="w-full sm:w-auto sm:self-start">
                      <Badge
                        tone={!masterIsEnabled ? "neutral" : masterAgentOnline ? "ok" : "warn"}
                        icon={!masterIsEnabled ? AlertTriangle : masterAgentOnline ? Wifi : WifiOff}
                        className="w-full justify-center sm:w-auto"
                      >
                        {!masterIsEnabled ? t("copyTrading.access.inactive") : masterAgentOnline ? t("copyTrading.status.connected") : t("copyTrading.status.waiting")}
                      </Badge>
                    </div>
                  }
                >
                  {!masterIsEnabled ? (
                    <div className="rounded-3xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
                      {t("copyTrading.access.activateFirst")}
                    </div>
                  ) : (
                    <div className="space-y-4 sm:space-y-5">
                      <div className="rounded-3xl border border-black/10 bg-white/60 p-5 sm:p-6 dark:border-white/10 dark:bg-white/[0.05]">
                        <div className="text-sm font-semibold text-neutral-900 dark:text-white">{t("copyTrading.access.useCasesTitle")}</div>
                        {/* Les cas d'usage à gauche, l'entrée du guide à droite :
                            l'installation sur le terminal copieur ne tient pas
                            dans une puce, et c'est là que l'utilisateur bloque. */}
                        <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <ul className="min-w-0 flex-1 space-y-2 text-sm leading-6 text-neutral-600 dark:text-white/70">
                            <li>• {t("copyTrading.access.useCase1")}</li>
                            <li>• {t("copyTrading.access.useCase2")}</li>
                          </ul>
                          <Btn
                            variant="ghost"
                            leftIcon={BookOpen}
                            onClick={() => setAccessGuideOpen(true)}
                            className="w-full shrink-0 sm:w-auto"
                          >
                            {t("copyTrading.access.guideCta")}
                          </Btn>
                        </div>
                      </div>

                      <div className="rounded-3xl border border-black/10 bg-white/60 p-5 sm:p-6 dark:border-white/10 dark:bg-white/[0.05]">
                        <div className="text-sm font-semibold text-neutral-900 dark:text-white">{t("copyTrading.access.createTitle")}</div>
                        <div className="mt-2 text-sm leading-6 text-neutral-600 dark:text-white/70">
                          {t("copyTrading.access.createText")}
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                          <Field label={t("copyTrading.access.nameOptional")} hint={t("copyTrading.access.nameHint")}>
                            <Input leftIcon={Tag} value={newTokLabel} onChange={(e) => setNewTokLabel(e.target.value)} placeholder={t("copyTrading.access.namePlaceholder")} />
                          </Field>
                          <Btn leftIcon={Plus} onClick={createPublicToken} disabled={tokensBusy || busy || loading || !masterIsEnabled} className="w-full sm:w-auto sm:min-w-[180px]">
                            {t("copyTrading.access.createAccess")}
                          </Btn>
                        </div>
                      </div>

                      <div className="rounded-3xl border border-black/10 bg-white/60 p-5 sm:p-6 dark:border-white/10 dark:bg-white/[0.05]">
                        <div className="text-sm font-semibold text-neutral-900 dark:text-white">{t("copyTrading.access.installTitle")}</div>
                        <div className="mt-2 text-sm leading-6 text-neutral-600 dark:text-white/70">
                          {t("copyTrading.access.installText")}
                        </div>
                        <div className="mt-4">
                          <Btn leftIcon={FileDown} onClick={() => downloadFromPublic(AGENTS_PUBLIC.follower.path, AGENTS_PUBLIC.follower.filename)} className="w-full sm:w-auto">
                            {t("copyTrading.actions.downloadAgent")}
                          </Btn>
                        </div>
                      </div>

                      <div className="rounded-3xl border border-black/10 bg-white/60 p-5 sm:p-6 dark:border-white/10 dark:bg-white/[0.05]">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <div>
                            <div className="text-sm font-semibold text-neutral-900 dark:text-white">{t("copyTrading.access.activeTitle")}</div>
                            <div className="mt-1 text-sm leading-6 text-neutral-600 dark:text-white/70">
                              {t("copyTrading.access.activeText")}
                            </div>
                          </div>
                          <IconBtn
                            onClick={() => setRevealTokens((v) => !v)}
                            aria-label={revealTokens ? t("copyTrading.access.hide") : t("copyTrading.access.show")}
                            disabled={!publicTokens?.length}
                            title={revealTokens ? t("copyTrading.access.hide") : t("copyTrading.access.show")}
                            className="self-start sm:self-auto"
                          >
                            {revealTokens ? <EyeOff size={16} /> : <Eye size={16} />}
                          </IconBtn>
                        </div>

                        <div className="mt-4 space-y-3">
                          {tokensLoading ? (
                            Array.from({ length: 3 }).map((_, i) => (
                              <div key={i} className="h-20 animate-pulse rounded-3xl border border-black/10 bg-white/60 dark:border-white/10 dark:bg-white/[0.05]" />
                            ))
                          ) : publicTokens.length ? (
                            publicTokens.map((tok) => (
                              <div key={String(tok.id)} className="rounded-3xl border border-black/10 bg-white/60 p-4 sm:p-5 dark:border-white/10 dark:bg-white/[0.04]">
                                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                      <div className="text-sm font-semibold text-neutral-900 dark:text-white">{tok.label ? tok.label : t("copyTrading.access.copierAccess")}</div>
                                      {tok.revoked ? (
                                        <Badge tone="bad" icon={XCircle}>{t("copyTrading.access.revoked")}</Badge>
                                      ) : (
                                        <Badge tone="ok" icon={CheckCircle2}>{t("copyTrading.access.active")}</Badge>
                                      )}
                                    </div>
                                    <div className="mt-3 rounded-2xl border border-black/10 bg-white/75 px-3 py-3 font-mono text-sm font-semibold text-neutral-900 dark:border-white/10 dark:bg-[#050214]/60 dark:text-white">
                                      <span className="block truncate">{revealTokens ? tok.token || "—" : maskToken(tok.token)}</span>
                                    </div>
                                  </div>
                                  <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:min-w-[132px]">
                                    <Btn variant="ghost" size="sm" leftIcon={CopyIcon} onClick={() => onCopy(tok.token)} disabled={!tok.token} className="w-full">
                                      {t("copyTrading.actions.copy")}
                                    </Btn>
                                    <Btn variant="danger" size="sm" leftIcon={Trash2} onClick={() => revokePublicToken(tok)} disabled={tokensBusy} className="w-full">
                                      {t("copyTrading.access.revoke")}
                                    </Btn>
                                  </div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="rounded-3xl border border-black/10 bg-white/60 p-4 text-sm leading-6 text-neutral-700 dark:border-white/10 dark:bg-white/[0.05] dark:text-white/75">
                              {t("copyTrading.access.empty")}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </Panel>

                {/* Guide complet de l'accès copieur */}
                <BottomSheet
                  open={accessGuideOpen}
                  onClose={() => setAccessGuideOpen(false)}
                  title={t("copyTrading.accessGuide.title")}
                  subtitle={t("copyTrading.accessGuide.subtitle")}
                  footer={
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <Btn variant="ghost" leftIcon={CopyIcon} size="lg" onClick={() => onCopy(PLATFORM_URL)}>
                        {t("copyTrading.accessGuide.copyUrl")}
                      </Btn>
                      <Btn leftIcon={FileDown} size="lg" onClick={() => downloadFromPublic(AGENTS_PUBLIC.follower.path, AGENTS_PUBLIC.follower.filename)}>
                        {t("copyTrading.actions.downloadAgent")}
                      </Btn>
                    </div>
                  }
                >
                  <ol className="space-y-3">
                    {(() => {
                      const steps = t("copyTrading.accessGuide.steps", { returnObjects: true });
                      return Array.isArray(steps) ? steps : [];
                    })().map((step, i) => (
                      <li key={i} className="rounded-2xl border border-black/10 bg-neutral-50 p-4 sm:p-5 dark:border-white/10 dark:bg-white/[0.04]">
                        <div className="flex items-center gap-2.5">
                          <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-500/12 text-[11px] font-semibold text-violet-600 dark:bg-violet-400/15 dark:text-violet-300">
                            {i + 1}
                          </span>
                          <div className="text-sm font-semibold text-neutral-900 dark:text-white">{step.title}</div>
                        </div>
                        {/* Le corps repart de la marge de la carte : aligné sous
                            la pastille, il se lirait comme une sous-liste. */}
                        <div className="mt-2 text-sm leading-6 text-neutral-600 dark:text-white/70">{step.text}</div>
                      </li>
                    ))}
                  </ol>

                  <div className="mt-3 rounded-2xl border border-amber-500/25 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900 dark:border-amber-400/25 dark:bg-amber-500/10 dark:text-amber-100">
                    {t("copyTrading.accessGuide.warning")}
                  </div>

                  {/* Le guide complet vit dans le Centre d'aide : cette modale
                      en donne le chemin le plus court, elle ne le recopie pas —
                      deux textes séparés finissent toujours par diverger. */}
                  <button
                    type="button"
                    onClick={() => navigate(helpRoute("copy-trading"))}
                    className="mt-3 flex w-full items-center justify-between gap-3 rounded-2xl border border-black/10 bg-neutral-50 px-4 py-3 text-left text-sm font-semibold text-neutral-900 hover:bg-neutral-100 dark:border-white/10 dark:bg-white/[0.04] dark:text-white dark:hover:bg-white/[0.07]"
                  >
                    <span className="flex items-center gap-2.5">
                      <BookOpen size={16} className="shrink-0 text-violet-500" />
                      {t("copyTrading.accessGuide.guideMore")}
                    </span>
                    <ChevronRight size={16} className="shrink-0 opacity-60" />
                  </button>
                </BottomSheet>

                <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2">
                  <KpiSmall label={t("copyTrading.kpi.followers")} value={safeNum(masterMe?.followers_count ?? masterMe?.followers, 0)} icon={Users} />
                  <KpiSmall label={t("copyTrading.kpi.trades")} value={safeNum(masterMe?.trades, 0)} icon={Repeat2} />
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
      </div>
    </div>
  );
}